import { Link, useRouterState } from '@tanstack/react-router'
import {
    LayoutDashboard,
    Users,
    Layers,
    ListChecks,
    ScrollText,
    Settings,
    Siren,
    BarChart3,
    Coins,
    UserCog,
    Handshake,
    Globe2,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useRoles } from '@/hooks/useRoles'
import type { OperatorRole } from '@/types/api'

interface NavItem {
    to: string
    label: string
    icon: React.ComponentType<{ className?: string }>
    roles?: OperatorRole[]
}

const NAV: NavItem[] = [
    { to: '/', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/players', label: 'Players', icon: Users },
    { to: '/tier-policy', label: 'Tier policy', icon: Layers },
    { to: '/jurisdictions', label: 'Jurisdictions', icon: Globe2 },
    { to: '/operations', label: 'Operations', icon: ListChecks },
    { to: '/volume', label: 'Volume', icon: BarChart3 },
    { to: '/pots', label: 'Pots', icon: Coins, roles: ['admin'] },
    { to: '/mlro', label: 'MLRO', icon: Siren, roles: ['mlro'] },
    { to: '/operators', label: 'Operators', icon: UserCog, roles: ['admin'] },
    { to: '/referrals', label: 'Referrals', icon: Handshake, roles: ['admin'] },
    { to: '/audit-log', label: 'Audit log', icon: ScrollText },
    { to: '/settings', label: 'Settings', icon: Settings },
]

export function Sidebar() {
    const { location } = useRouterState()
    const { hasAnyRole } = useRoles()
    return (
        <aside className="border-sidebar-border bg-sidebar text-sidebar-foreground flex w-60 flex-col border-r">
            <div className="border-sidebar-border flex h-14 items-center gap-2 border-b px-4">
                <img src="/logo.svg" alt="Blockpot" className="h-6 w-6 rounded" />
                <div className="flex flex-col leading-none">
                    <span className="text-sm font-semibold">Blockpot</span>
                    <span className="text-muted-foreground text-[11px] tracking-wide uppercase">
                        Operator Console
                    </span>
                </div>
            </div>
            <nav className="flex-1 px-2 py-3">
                {NAV.map((item) => {
                    if (item.roles && !hasAnyRole(...item.roles)) return null
                    const active =
                        item.to === '/'
                            ? location.pathname === '/'
                            : location.pathname.startsWith(item.to)
                    const Icon = item.icon
                    return (
                        <Link
                            key={item.to}
                            to={item.to}
                            className={cn(
                                'flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors',
                                active
                                    ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                                    : 'text-muted-foreground hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground',
                            )}
                        >
                            <Icon className="h-4 w-4" />
                            {item.label}
                        </Link>
                    )
                })}
            </nav>
            <div className="border-sidebar-border text-muted-foreground border-t px-4 py-3 text-[11px]">
                v0.1.0 · MVP
            </div>
        </aside>
    )
}
